import SchemaProvider from 'titus-ts/dist/js/MetadataRenderer/Schema/SchemaProvider';
import {
    ClassificationEventName,
    ClassificationEventType,
    EmailAttachment,
    EmailMessage,
    EmailRecipient,
    EventPayload,
    Identity,
    MessageContext,
    Metadata,
    SeverityLevel,
    MessageEvent,
} from './auditEventTypes';
import AppService from '../../services/appService';
import { IItemAuditDataProvider } from './auditDataProvider';
import { OfficeApi } from '../../office/OfficeApi';
import { localSettingsService } from '../localSettingsService';

export interface IMessageDataProvider {
    getMessageAuditProperties(): Promise<EmailMessage>;
}
export interface IMessageContextDataProvider {
    getMessageContextAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<MessageContext>;
}

export class MessageAuditDataProvider
    implements IMessageDataProvider, IMessageContextDataProvider, IItemAuditDataProvider
{
    private officeApi: OfficeApi;
    private schemaProvider: SchemaProvider;
    private appService: AppService;

    constructor(officeApi: OfficeApi, schemaProvider: SchemaProvider, appService: AppService) {
        this.officeApi = officeApi;
        this.schemaProvider = schemaProvider;
        this.appService = appService;
    }

    public async getItemAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<EventPayload> {
        const [message, messageContext] = await Promise.all([
            this.getMessageAuditProperties(),
            this.getMessageContextAuditProperties(event, eventType),
        ]);

        return {
            MessageContext: messageContext,
            Message: message,
        } as MessageEvent;
    }

    public async getMessageAuditProperties(): Promise<EmailMessage> {
        const [itemType, subject, sender, recipients, attachments, metadata, originalMetadata] = await Promise.all([
            this.getItemType(),
            this.getSubject(),
            this.getSender(),
            this.getAllRecipients(),
            this.getAttachments(),
            this.getMetadata(),
            this.getOriginalMetadata(),
        ]);

        const messageAuditProperties = {
            Type: itemType,
            Subject: subject,
            Sender: sender,
            Recipients: recipients,
            Attachments: attachments,
            AttachmentCount: attachments.length,
            Metadata: metadata,
            OriginalMetadata: originalMetadata,
            OriginalField: {},
            NewField: {},
        } as EmailMessage;

        return messageAuditProperties;
    }

    private async getItemType(): Promise<EmailMessage['Type']> {
        const itemType = this.officeApi.getItemType();
        if (itemType === 'message' || itemType === 'appointment') {
            return itemType;
        }

        return 'unknown';
    }

    public async getMessageContextAuditProperties(
        event: ClassificationEventName,
        eventType: ClassificationEventType,
    ): Promise<MessageContext> {
        const messageContextAuditProperties = {
            UserName: this.getUserName(),
            Event: event,
            EventType: eventType,
            Product: this.getProductName(),
            Version: this.getProductVersion(),
            Platform: this.getPlatform(),
            Severity: this.getSeverity(event, eventType),
        } as MessageContext;

        return messageContextAuditProperties;
    }

    private async getSubject(): Promise<string> {
        return this.officeApi.getSubjectAsync();
    }

    private getSender(): Identity {
        return {
            Address: this.officeApi.getSenderAddress(),
            DisplayName: this.officeApi.getSenderDisplayName(),
        } as Identity;
    }

    private async getAllRecipients(): Promise<EmailRecipient[]> {
        const messageRecipients = (
            await Promise.all([this.officeApi.getToAsync(), this.officeApi.getCcAsync(), this.officeApi.getBccAsync()])
        ).flatMap((r) => r ?? []);

        const auditEmailRecipients = messageRecipients.map(
            (r) =>
                ({
                    Address: r.Address,
                    DisplayName: r.DisplayName,
                    RecipientType: r.RecipientType as EmailRecipient['RecipientType'],
                    IsDistributionList: r.IsDistributionList,
                }) as EmailRecipient,
        );

        return auditEmailRecipients;
    }

    private async getAttachments(): Promise<EmailAttachment[]> {
        const messageAttachments = await this.officeApi.getAttachments();

        const auditAttachments = messageAttachments.map(
            (a) =>
                ({
                    Type: 'unknown',
                    Name: a.FileName,
                    Size: a.SizeKB,
                    Metadata: undefined,
                }) as EmailAttachment,
        );

        return auditAttachments;
    }

    private getMetadata(): Metadata {
        const metadata = this.schemaProvider.getMetadata();

        const auditMetadata = Object.entries(metadata).map(([key, value]) => ({
            FieldName: key,
            Values: (Array.isArray(value) ? value : [value]).map((v) => ({
                Value: v,
            })),
        }));

        return auditMetadata;
    }

    private getOriginalMetadata(): Metadata {
        const originalMetadata = this.schemaProvider.getOriginalMetadata();

        const auditOriginalMetadata = Object.entries(originalMetadata).map(([key, value]) => ({
            FieldName: key,
            Values: (Array.isArray(value) ? value : [value]).map((v) => ({
                Value: v,
            })),
        }));

        return auditOriginalMetadata;
    }

    private getUserName(): string {
        return this.officeApi.getSenderAddress();
    }

    private getProductName(): string {
        return this.appService.getProductName();
    }

    private getProductVersion(): string {
        return localSettingsService.getLocalSettings().dcsOneAboutInfo.version;
    }

    private getPlatform(): string {
        return this.officeApi.getPlatformType() || '';
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getSeverity(event: ClassificationEventName, eventType: ClassificationEventType): SeverityLevel {
        return 'Informational' as SeverityLevel;
    }
}
